import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import api from "../api";
import { logoutUser, signInUser } from "../api/auth";

// --- Types ---
export interface UserData {
  id: string;
  name: string;
  email: string;
  role?: string;
}
interface AuthResponse {
  data: any;
  accessToken: string;
  refresh_token: string;
  expires_in: number; // Unix timestamp
  message?: string;
  user?: UserData;
  permissions?: any[]; // Add this line to fix the error
}

// --- Hook: Sign In ---
export const useSignIn = () => {
  const navigate = useNavigate();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to schedule auto refresh 5 mins before expiry
  const scheduleRefresh = (expires_in: number, refresh_token: string) => {
    const now = dayjs().unix();
    const delay = (expires_in - now - 300) * 1000; // 5 min before expiry

    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    if (delay > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshSession(refresh_token);
      }, delay);
    } else {
      refreshSession(refresh_token);
    }
  };

  // --- API Call: Refresh session tokens ---
  const refreshSession = async (refresh_token: string) => {
    try {
      const { data } = await api.post<AuthResponse>("/admin-session/v1", {
        refresh_token,
      });

      if (data?.accessToken && data?.refresh_token) {
        Cookies.set("userToken", data.accessToken, {
          secure: true,
          sameSite: "Strict",
        });

        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("expires_in", data.expires_in.toString());
        scheduleRefresh(data.expires_in, data.refresh_token);
      } else {
        console.error("Failed to refresh session", data);
      }
    } catch (err) {
      console.error("Error refreshing session", err);
      message.error("Session expired. Please log in again.");
      Cookies.remove("userToken");
      localStorage.clear();
      navigate("/login");
    }
  };

  // --- Mutation: Sign in user ---
  const signInMutation = useMutation({
    mutationFn: signInUser,
    onSuccess: (data: AuthResponse) => {
      if (data?.accessToken && data?.refresh_token) {
        message.success("Login successful");

        // ✅ Store tokens
        Cookies.set("userToken", data.accessToken, {
          expires: 1, // 1 day
          secure: true,
          sameSite: "Strict",
        });
        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("expires_in", data.expires_in.toString());
        localStorage.setItem("permissionsLoaded", "false");

        if (data) {
          localStorage.setItem("Data", JSON.stringify(data));
        }

        if (data?.data.permissions) {
          // Option 1: store in cookies
          Cookies.set(
            "userPermissions",
            JSON.stringify(data.data.permissions),
            {
              secure: true,
              sameSite: "Strict",
            }
          );

          // Option 2: store in localStorage (optional)
          localStorage.setItem(
            "userPermissions",
            JSON.stringify(data.data.permissions)
          );
        }
        // ✅ Schedule auto refresh
        scheduleRefresh(data.expires_in, data.refresh_token);

        const permissions = data?.data?.permissions || [];

        const firstAllowedModule = permissions.find((p: any) => p.view);

        if (firstAllowedModule) {
          switch (firstAllowedModule.module_id) {
            case 1:
              navigate("/dashboard");
              break;
            case 2:
              navigate("/tickets");
              break;
            case 3:
              navigate("/user"); // Members
              break;
            case 4:
              navigate("/unit");
              break;
            case 5:
              navigate("/user-role");
              break;
            default:
              navigate("/dashboard");
          }
        } else {
          // No view permission anywhere = logout
          message.error("No permission to access any module");
          Cookies.remove("userToken");
          localStorage.clear();
          navigate("/login");
        }
      } else {
        message.error("Invalid login response");
      }
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong!"
      );
    },
  });

  // --- Auto schedule refresh if already logged in ---
  useEffect(() => {
    const refreshToken = localStorage.getItem("refresh_token");
    const expiresIn = Number(localStorage.getItem("expires_in"));
    if (refreshToken && expiresIn) {
      scheduleRefresh(expiresIn, refreshToken);
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  return signInMutation;
};

// --- Hook: Logout ---
export const useLogout = () => {
  const navigate = useNavigate();

  return useMutation({
    // ✅ use the argument provided by mutate()
    mutationFn: (refresh_token: string) => logoutUser(refresh_token),

    onSuccess: (data) => {
      message.success(data?.message || "Logged out successfully");

      Cookies.remove("userToken");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("expires_in");
      localStorage.removeItem("Data");
      sessionStorage.clear();

      navigate("/login");
    },
    onError: (error: any) => {
      message.error(error?.message || "Logout failed");
    },
  });
};
